import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { randomboxApi } from '../../api/randombox.js';
import type { RbProduct, RbProductInput } from '../../api/types.js';
import {
  ErrorBox,
  Pagination,
  Field,
  FilterBar,
  ProductCell,
  SortableTh,
  SearchInput,
} from '../../components/ui.js';
import { Modal } from '../../components/Modal.js';
import { QueryState } from '../../components/QueryState.js';
import { useUrlUpdate, useUrlSearch } from '../../lib/hooks.js';
import { useTableSort } from '../../lib/useTableSort.js';
import { text, money, num, dateOnly } from '../../lib/format.js';
import {
  checkImageUrl,
  checkLen,
  checkRequired,
  checkDateRange,
  hasFieldErrors,
  inputCls,
  type FieldErrors,
} from '../../lib/formValidation.js';

/** 원본: randombox/product/product_list.php */
export function RbProductsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? '1');
  const sort = params.get('sort') ?? 'randomboxId';
  const dir = (params.get('dir') ?? 'desc') as 'asc' | 'desc';
  const categoryCode = params.get('categoryCode') ?? '';
  const status = params.get('status') ?? '0';

  const update = useUrlUpdate(params, setParams);
  const [search, setSearch] = useUrlSearch(params, update);
  const debounced = params.get('q') ?? '';
  const th = useTableSort(sort, dir, update);

  const [formModal, setFormModal] = useState<{ mode: 'edit' | 'copy'; product: RbProduct } | null>(null);

  const categories = useQuery({
    queryKey: ['randombox', 'categories', 'all'],
    queryFn: () => randomboxApi.categories({ page: 1, perPage: 200 }),
    staleTime: 10 * 60 * 1000,
  });

  const query = useQuery({
    queryKey: ['randombox', 'products', { page, sort, dir, q: debounced, categoryCode, status }],
    queryFn: () =>
      randomboxApi.products({
        page,
        perPage: 30,
        sort,
        dir,
        q: debounced || undefined,
        categoryCode: categoryCode || undefined,
        status,
      }),
    placeholderData: keepPreviousData,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['randombox', 'products'] });
  }

  return (
    <section>
      <header className="page-head">
        <h1 className="page-title">랜덤박스 관리</h1>
        {query.isFetching && <span className="page-head__busy">갱신 중…</span>}
      </header>

      <FilterBar
        onReset={() => {
          setSearch('');
          setParams(new URLSearchParams(), { replace: true });
        }}
      >
        <Field label="검색 (상품명 · 이벤트메모)">
          <SearchInput
            value={search}
            placeholder="상품명"
            onChange={setSearch}
          />
        </Field>
        <Field label="카테고리">
          <select className="input" value={categoryCode} onChange={(e) => update({ categoryCode: e.target.value })}>
            <option value="">전체</option>
            {categories.data?.items.map((c) => (
              <option key={c.companyCode ?? c.seq} value={c.companyCode ?? ''}>
                {text(c.companyName)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="노출 상태">
          <select className="input" value={status} onChange={(e) => update({ status: e.target.value })}>
            <option value="0">노출 중</option>
            <option value="1">종료 · 숨김</option>
          </select>
        </Field>
      </FilterBar>

      <QueryState query={query} empty={query.data?.items.length === 0}>
        {query.data && (
          <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <SortableTh label="ID" sortKey="randomboxId" {...th} align="right" />
                  <th>상품</th>
                  <th>카테고리</th>
                  <SortableTh label="가격" sortKey="price" {...th} align="right" />
                  <SortableTh label="판매 시작" sortKey="saleStart" {...th} />
                  <SortableTh label="판매 종료" sortKey="saleEnd" {...th} />
                  <SortableTh label="노출 순위" sortKey="priority" {...th} align="right" />
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((p, i) => (
                  <tr
                    key={p.randomboxId ?? i}
                    className={p.randomboxId ? 'table__row--clickable' : undefined}
                    onClick={
                      p.randomboxId
                        ? () => navigate(`/randombox/products/${p.randomboxId}/prizes`)
                        : undefined
                    }
                  >
                    <td className="td-right td-muted">{num(p.randomboxId)}</td>
                    <td>
                      <ProductCell name={text(p.name)} thumbnail={p.thumbnail} />
                    </td>
                    <td>{text(p.categoryName)}</td>
                    <td className="td-right">{money(p.price)}</td>
                    <td className="td-muted">{dateOnly(p.saleStart)}</td>
                    <td className="td-muted">{dateOnly(p.saleEnd)}</td>
                    <td className="td-right">{num(p.priorityRank)}</td>
                    {/* 행 클릭은 경품 관리로 이동하므로, 관리 칸에서는 이동을 막는다. */}
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="toolbar toolbar--tight">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setFormModal({ mode: 'edit', product: p })}
                        >
                          수정
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setFormModal({ mode: 'copy', product: p })}
                        >
                          복사
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={query.data.page}
            totalPages={query.data.totalPages}
            total={query.data.total}
            onChange={(p) => update({ page: p }, false)}
          />
          </>
        )}
      </QueryState>

      {formModal && (
        <ProductFormModal
          mode={formModal.mode}
          product={formModal.product}
          categories={categories.data?.items ?? []}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            setFormModal(null);
            refresh();
          }}
        />
      )}
    </section>
  );
}

interface ProductFormState {
  categoryCode: string;
  name: string;
  saleStart: string;
  saleEnd: string;
  thumbnail: string;
  movie: string;
  image1: string;
  image2: string;
  price: string;
  priorityRank: string;
  orderColor: string;
  memo: string;
}

function toProductFormState(p: RbProduct): ProductFormState {
  return {
    categoryCode: p.categoryCode ?? '',
    name: p.name ?? '',
    saleStart: dateOnly(p.saleStart) === '-' ? '' : dateOnly(p.saleStart),
    saleEnd: dateOnly(p.saleEnd) === '-' ? '' : dateOnly(p.saleEnd),
    thumbnail: p.thumbnail ?? '',
    movie: p.movie ?? '',
    image1: p.image1 ?? '',
    image2: p.image2 ?? '',
    price: p.price !== null && p.price !== undefined ? String(p.price) : '',
    priorityRank: p.priorityRank !== null && p.priorityRank !== undefined ? String(p.priorityRank) : '0',
    orderColor: p.orderColor || '#ffffff',
    memo: p.memo ?? '',
  };
}

/**
 * 판매 시작/종료는 날짜만 입력받지만, 원본 product_modify.php 는 시작은
 * 18:00:00, 종료는 23:59:59 를 붙여 저장한다 — 이 시간이 실제 판매 오픈/마감
 * 시각으로 스토어프론트에서 쓰이므로 그대로 옮긴다.
 */
function toSaleDateTime(dateStr: string, endOfDay: boolean): string {
  return `${dateStr} ${endOfDay ? '23:59:59' : '18:00:00'}`;
}

function toProductInput(form: ProductFormState): RbProductInput {
  return {
    categoryCode: form.categoryCode,
    name: form.name,
    saleStart: toSaleDateTime(form.saleStart, false),
    saleEnd: toSaleDateTime(form.saleEnd, true),
    thumbnail: form.thumbnail,
    movie: form.movie,
    image1: form.image1,
    image2: form.image2,
    price: Number(form.price) || 0,
    priorityRank: form.priorityRank === '' ? 0 : Number(form.priorityRank),
    orderColor: form.orderColor,
    memo: form.memo,
  };
}

/** 상품 수정/복사 모달. 원본 product_modify.php · product_copy.php 를 합쳤다. */
function ProductFormModal({
  mode,
  product,
  categories,
  onClose,
  onSaved,
}: {
  mode: 'edit' | 'copy';
  product: RbProduct;
  categories: { companyCode: string | null; companyName: string | null }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<ProductFormState>(() => toProductFormState(product));
  const randomboxId = Number(product.randomboxId);

  const [submitted, setSubmitted] = useState(false);
  /** 규칙은 서버 COMMON_PRODUCT_FIELDS(product-crud.ts)와 같은 값이다. */
  const errors: FieldErrors = {
    name: checkRequired(form.name, '랜덤박스명', 255),
    thumbnail: checkImageUrl(form.thumbnail),
    movie: checkImageUrl(form.movie),
    image1: checkImageUrl(form.image1),
    image2: checkImageUrl(form.image2),
    orderColor: checkLen(form.orderColor, 20, '테마 색상'),
    memo: checkLen(form.memo, 5000, '관리용 메모'),
    saleEnd: checkDateRange(form.saleStart, form.saleEnd),
  };
  const shown: FieldErrors = submitted ? errors : {};

  const save = useMutation({
    mutationFn: () => {
      const body = toProductInput(form);
      return mode === 'edit'
        ? randomboxApi.updateProduct(randomboxId, body)
        : randomboxApi.copyProduct(randomboxId, body);
    },
    onSuccess: onSaved,
  });

  const valid = form.categoryCode && form.name && form.saleStart && form.saleEnd && form.price !== '';

  return (
    <Modal title={mode === 'edit' ? '랜덤박스 수정' : '랜덤박스 복사'} onClose={onClose}>
      {mode === 'copy' && (
        <p className="hint">
          <b>{text(product.name)}</b>(#{product.randomboxId})의 내용을 그대로 새 랜덤박스로
          만듭니다. 등록된 경품도 함께 복제됩니다. 필요하면 아래 값을 고친 뒤 저장하세요.
        </p>
      )}

        <form
          className="form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(true);
            if (hasFieldErrors(errors)) return;
            save.mutate();
          }}
        >
          <Field label="카테고리">
            <select
              className="input"
              value={form.categoryCode}
              onChange={(e) => setForm((f) => ({ ...f, categoryCode: e.target.value }))}
            >
              <option value="">카테고리 선택</option>
              {categories.map((c) => (
                <option key={c.companyCode} value={c.companyCode ?? ''}>
                  {c.companyName} ({c.companyCode})
                </option>
              ))}
            </select>
          </Field>

          <Field label="랜덤박스명" error={shown.name}>
            <input
              className={inputCls(shown.name)}
              value={form.name}
                maxLength={255}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </Field>

          <div className="row-2">
            <Field label="판매 시작">
              <input
                className="input"
                type="date"
                value={form.saleStart}
                onChange={(e) => setForm((f) => ({ ...f, saleStart: e.target.value }))}
              />
            </Field>
            <Field label="판매 종료" error={shown.saleEnd}>
              <input
                className={inputCls(shown.saleEnd)}
                type="date"
                value={form.saleEnd}
                onChange={(e) => setForm((f) => ({ ...f, saleEnd: e.target.value }))}
              />
            </Field>
          </div>

          <Field label="대표 이미지" error={shown.thumbnail}>
            <div className="img-input-row">
              <input
                className={inputCls(shown.thumbnail)}
                value={form.thumbnail}
                maxLength={255}
                placeholder="https://..."
                onChange={(e) => setForm((f) => ({ ...f, thumbnail: e.target.value }))}
              />
              {form.thumbnail && <img className="thumb thumb--lg" src={form.thumbnail} alt="" />}
            </div>
          </Field>

          <Field label="동영상 URL" error={shown.movie}>
            <input
              className={inputCls(shown.movie)}
              value={form.movie}
                maxLength={255}
              placeholder="https://..."
              onChange={(e) => setForm((f) => ({ ...f, movie: e.target.value }))}
            />
          </Field>

          <div className="row-2">
            <Field label="소개 이미지 1" error={shown.image1}>
              <div className="img-input-row">
                <input
                  className={inputCls(shown.image1)}
                  value={form.image1}
                maxLength={255}
                  onChange={(e) => setForm((f) => ({ ...f, image1: e.target.value }))}
                />
                {form.image1 && <img className="thumb thumb--lg" src={form.image1} alt="" />}
              </div>
            </Field>
            <Field label="소개 이미지 2" error={shown.image2}>
              <div className="img-input-row">
                <input
                  className={inputCls(shown.image2)}
                  value={form.image2}
                maxLength={255}
                  onChange={(e) => setForm((f) => ({ ...f, image2: e.target.value }))}
                />
                {form.image2 && <img className="thumb thumb--lg" src={form.image2} alt="" />}
              </div>
            </Field>
          </div>

          <div className="row-2">
            <Field label="판매 가격">
              <input
                className="input"
                type="number"
                min={0}
                value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              />
            </Field>
            <Field label="표시 순위">
              <input
                className="input"
                type="number"
                value={form.priorityRank}
                onChange={(e) => setForm((f) => ({ ...f, priorityRank: e.target.value }))}
              />
            </Field>
          </div>

          <Field label="테마 색상" error={shown.orderColor}>
            <div className="color-input-row">
              <input
                className="input color-input"
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(form.orderColor) ? form.orderColor : '#ffffff'}
                onChange={(e) => setForm((f) => ({ ...f, orderColor: e.target.value }))}
              />
              <input className={inputCls(shown.orderColor)} value={form.orderColor}
                maxLength={20} readOnly />
            </div>
          </Field>

          <Field label="관리용 메모" error={shown.memo}>
            <input
              className={inputCls(shown.memo)}
              value={form.memo}
                maxLength={5000}
              onChange={(e) => setForm((f) => ({ ...f, memo: e.target.value }))}
            />
          </Field>

          {/* 오류 모양은 화면 안에서 한 가지만 쓴다 - 예전에는 이 자리만
              경품 재고용 클래스(qty-msg)를 빌려 썼다. */}
          {save.isError && <ErrorBox error={save.error} />}

          <button type="submit" className="btn btn--primary btn--block" disabled={!valid || save.isPending}>
            {save.isPending ? '저장 중…' : mode === 'edit' ? '저장하기' : '복사본 만들기'}
          </button>
      </form>
    </Modal>
  );
}
